import { Badge, Flex, Paper, Text, useMantineTheme } from '@mantine/core';
import React from 'react';
import type { SummaryCardRow } from './SummaryCard.types.ts';

interface Props {
	title: string;
	rows: SummaryCardRow[];
	// Shown top-right, for a card whose contents were vouched for by something.
	badgeLabel?: string;
	// Stands in for the rows when there are none to list. Omit it and an empty card
	// renders its heading alone.
	emptyText?: string;
	// Outlines the card in the success colour, so a card carrying confirmed values is
	// distinguishable from one merely showing what is stored.
	highlighted?: boolean;
	dataTestId?: string;
}

// A read-only card of label-and-value pairs under a heading: the shape used wherever
// a set of details is summarised back to the reader rather than edited.
export const SummaryCard = ({
	title,
	rows,
	badgeLabel,
	emptyText,
	highlighted = false,
	dataTestId,
}: Props) => {
	const theme = useMantineTheme();

	return (
		<Paper
			withBorder
			p="0.5rem"
			style={highlighted ? { borderColor: theme.colors.teal[8] } : undefined}
			data-testid={dataTestId}
		>
			<Flex direction="column" gap="0.5rem">
				<Flex align="center" justify="space-between" gap="0.5rem">
					<Text fw={600} c="dark.8">
						{title}
					</Text>
					{badgeLabel && (
						<Badge
							variant="outline"
							color="teal.8"
							c="teal.8"
							size="sm"
							style={{ fontWeight: 400 }}
						>
							{badgeLabel}
						</Badge>
					)}
				</Flex>
				<Flex direction="column" gap="0.25rem">
					{rows.map((row) => (
						<Flex key={row.key ?? row.label} justify="space-between" gap="1rem">
							{/* The label holds one line and the value takes the slack, so a
							    long value wraps against the right edge instead of pushing
							    its own label onto a second line. */}
							<Text fz="sm" c="gray.9" style={{ whiteSpace: 'nowrap' }}>
								{row.label}
							</Text>
							<Text fz="sm" c="dark.8" fw={600} style={{ textAlign: 'right' }}>
								{row.value}
							</Text>
						</Flex>
					))}
					{rows.length === 0 && emptyText && (
						<Text fz="sm" c="gray.9">
							{emptyText}
						</Text>
					)}
				</Flex>
			</Flex>
		</Paper>
	);
};
